/**
 * AlertsPanel
 *
 * Slide-in panel that shows all active clinical alerts.
 * Opened from the bell icon in the Sidebar.
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClinicalAlert } from '../types';

interface Props {
  alerts: ClinicalAlert[];
  onClose: () => void;
  onViewResult: (resultId: string) => void;
  onDismiss: (alertId: string) => void;
  onDismissAll: () => void;
  onMarkAllRead: () => void;
}

export default function AlertsPanel({
  alerts,
  onClose,
  onViewResult,
  onDismiss,
  onDismissAll,
  onMarkAllRead,
}: Props) {
  const { t, i18n } = useTranslation('app');
  const critical = alerts.filter((a) => a.severity === 'critical');
  const warnings = alerts.filter((a) => a.severity === 'warning');

  // Mark all as read when panel opens
  useEffect(() => {
    onMarkAllRead();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">{t('alerts.title')}</h2>
              <p className="text-xs text-slate-500">{t('alerts.activeCount', { count: alerts.length })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {alerts.length > 0 && (
              <button
                onClick={onDismissAll}
                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
              >
                {t('alerts.dismissAll')}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-slate-700 font-semibold">{t('alerts.none')}</p>
              <p className="text-slate-400 text-sm mt-1">{t('alerts.noneDescription')}</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {/* Critical alerts first */}
              {critical.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-2 px-1">
                    🚨 {t('alerts.critical')} ({critical.length})
                  </p>
                  {critical.map((alert) => (
                    <AlertCard
                      key={alert.id}
                      alert={alert}
                      onView={() => { onViewResult(alert.testResultId); onClose(); }}
                      onDismiss={() => onDismiss(alert.id)}
                      formatDate={formatDate}
                      t={t}
                    />
                  ))}
                </div>
              )}

              {/* Warnings */}
              {warnings.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2 px-1">
                    ⚠️ {t('alerts.warnings')} ({warnings.length})
                  </p>
                  {warnings.map((alert) => (
                    <AlertCard
                      key={alert.id}
                      alert={alert}
                      onView={() => { onViewResult(alert.testResultId); onClose(); }}
                      onDismiss={() => onDismiss(alert.id)}
                      formatDate={formatDate}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-center">
          <p className="text-xs text-slate-400">{t('alerts.footer')}</p>
        </div>
      </div>
    </>
  );
}

// ─── AlertCard ────────────────────────────────────────────────────────────────

function AlertCard({
  alert,
  onView,
  onDismiss,
  formatDate,
  t,
}: {
  alert: ClinicalAlert;
  onView: () => void;
  onDismiss: () => void;
  formatDate: (iso: string) => string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const isCritical = alert.severity === 'critical';
  const isUnread = !alert.readAt;

  return (
    <div
      className={`relative rounded-xl border p-3.5 mb-2 ${
        isCritical
          ? 'border-red-200 bg-red-50'
          : 'border-amber-200 bg-amber-50'
      } ${isUnread ? 'ring-2 ring-offset-1 ring-red-300/50' : ''}`}
    >
      {isUnread && (
        <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500" />
      )}

      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isCritical ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
        }`}>
          {isCritical ? '🚨' : '⚠️'}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${isCritical ? 'text-red-900' : 'text-amber-900'}`}>
            {alert.patientName}
          </p>
          <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            {alert.message}
          </p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-xs text-slate-400">{alert.testCode}</span>
            {alert.triggeredScore !== null && (
              <span className="text-xs text-slate-400">· {t('alerts.score', { score: alert.triggeredScore })}</span>
            )}
            <span className="text-xs text-slate-400">· {formatDate(alert.triggeredAt)}</span>
          </div>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={onView}
              className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors ${
                isCritical
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-500 text-white hover:bg-amber-600'
              }`}
            >
              {t('alerts.viewReport')}
            </button>
            <button
              onClick={onDismiss}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              {t('alerts.dismiss')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
